import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ImageUpload from '../components/ImageUpload';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { deleteUserAccount } from '../services/userService';
import { auth } from '../config/firebase';
import { deleteUser } from 'firebase/auth';

const profileSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  phone: z.string().min(10, 'Telefone inválido').optional().or(z.literal('')),
  specialty: z.string().optional(),
  experience: z.string().optional(),
});

/**
 * Página: ProfilePage (/pages/ProfilePage.jsx)
 * Perfil do usuário com edição de dados e histórico
 */
const ProfilePage = () => {
  const { currentUser, appointments, barbershops, updateUserData, waitlist, removeFromWaitlist } = useAppContext();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('stats'); // 'info' | 'history' | 'stats' | 'waitlist'
  const [profilePhoto, setProfilePhoto] = useState(currentUser?.photoURL || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Redirecionar usuários não logados
  useEffect(() => {
    if (!currentUser) {
      toast.error('Você precisa fazer login para acessar seu perfil');
      navigate('/login', { state: { from: '/perfil' } });
    }
  }, [currentUser, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: currentUser?.name || '',
      phone: currentUser?.phone || '',
      specialty: currentUser?.specialty || '',
      experience: currentUser?.experience || '',
    }
  });

  // Agendamentos do usuário
  const userAppointments = useMemo(() => {
    return appointments
      .filter(app => app.userId === currentUser?.id)
      .sort((a, b) => new Date(b.date + 'T' + b.time) - new Date(a.date + 'T' + a.time));
  }, [appointments, currentUser]);

  // Estatísticas avançadas
  const stats = useMemo(() => {
    if (!currentUser) return null;

    const completed = userAppointments.filter(a => a.status === 'completed').length;
    const pending = userAppointments.filter(a => a.status === 'pending').length;
    const confirmed = userAppointments.filter(a => a.status === 'confirmed').length;
    const cancelled = userAppointments.filter(a => a.status === 'cancelled').length;
    
    // Calcular gasto total
    const totalSpent = userAppointments
      .filter(a => a.status === 'completed' && a.servicePrice)
      .reduce((sum, app) => sum + Number(app.servicePrice), 0);
    
    // Gasto médio por agendamento
    const avgSpent = completed > 0 ? totalSpent / completed : 0;
    
    // Barbearia mais visitada
    const barbershopCounts = {};
    userAppointments
      .filter(a => a.status === 'completed')
      .forEach(app => {
        barbershopCounts[app.barbershopId] = (barbershopCounts[app.barbershopId] || 0) + 1;
      });
    
    const mostVisitedId = Object.keys(barbershopCounts).reduce(
      (a, b) => barbershopCounts[a] > barbershopCounts[b] ? a : b,
      null
    );
    
    const mostVisited = mostVisitedId 
      ? barbershops.find(b => b.id === mostVisitedId)
      : null;

    // Serviço mais usado
    const serviceCounts = {};
    userAppointments
      .filter(a => a.status === 'completed')
      .forEach(app => {
        serviceCounts[app.service] = (serviceCounts[app.service] || 0) + 1;
      });
    
    const mostUsedService = Object.keys(serviceCounts).reduce(
      (a, b) => serviceCounts[a] > serviceCounts[b] ? a : b,
      null
    );

    // Agendamentos por mês (últimos 6 meses)
    const monthlyAppointments = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyAppointments[monthKey] = 0;
    }
    
    userAppointments
      .filter(a => a.status === 'completed')
      .forEach(app => {
        const monthKey = app.date.substring(0, 7); // YYYY-MM
        if (monthlyAppointments.hasOwnProperty(monthKey)) {
          monthlyAppointments[monthKey]++;
        }
      });

    return {
      completed,
      pending,
      confirmed,
      cancelled,
      total: userAppointments.length,
      totalSpent,
      avgSpent,
      mostVisited,
      mostUsedService,
      monthlyAppointments: Object.entries(monthlyAppointments).map(([month, count]) => ({
        month,
        count,
        monthName: new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
      }))
    };
  }, [userAppointments, barbershops, currentUser]);

  // Return early se não tiver usuário
  if (!currentUser) {
    return null;
  }

  const onSubmit = async (data) => {
    const updateData = {
      ...data,
      photoURL: profilePhoto,
    };
    
    const result = await updateUserData(currentUser.id, updateData);
    
    if (result?.success) {
      toast.success('Perfil atualizado com sucesso!');
      setIsEditing(false);
    } else {
      toast.error('Erro ao atualizar perfil');
    }
  };

  const handlePhotoUploaded = (url, path) => {
    setProfilePhoto(url);
  };

  const handleCancelEdit = () => {
    reset({
      name: currentUser?.name || '',
      phone: currentUser?.phone || '',
      specialty: currentUser?.specialty || '',
      experience: currentUser?.experience || '',
    });
    setIsEditing(false);
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // 1. Deletar dados do Firestore
      const result = await deleteUserAccount(currentUser.id);
      
      if (!result.success) {
        toast.error('Erro ao deletar dados da conta: ' + result.error);
        setIsDeleting(false);
        return;
      }

      // 2. Deletar autenticação do Firebase
      const user = auth.currentUser;
      if (user) {
        await deleteUser(user);
      }

      toast.success('Conta deletada com sucesso');
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Por segurança, faça login novamente e tente deletar sua conta');
        navigate('/login');
      } else {
        toast.error('Erro ao deletar conta: ' + error.message);
      }
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendente' },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmado' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Concluído' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Carregando perfil...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header do Perfil */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 lg:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              {/* Foto do Perfil ou Ícone */}
              {currentUser.photoURL ? (
                <img 
                  src={currentUser.photoURL} 
                  alt={currentUser.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0 border-2 border-indigo-200"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl sm:text-3xl font-bold text-indigo-600">
                    {currentUser.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{currentUser.name}</h1>
                <p className="text-sm sm:text-base text-gray-600 truncate">{currentUser.email}</p>
                <span className="inline-block mt-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800">
                  {currentUser.role === 'admin' ? 'Administrador' : 
                   currentUser.role === 'owner' ? 'Dono de Barbearia' : 'Cliente'}
                </span>
              </div>
            </div>
            
            {!isEditing && (
              <Button variant="secondary" onClick={() => setIsEditing(true)} className="w-full sm:w-auto whitespace-nowrap">
                {icons.pencil({ className: 'h-5 w-5 mr-2' })}
                Editar Perfil
              </Button>
            )}
          </div>

          {/* Formulário de Edição */}
          {isEditing && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 border-t pt-6">
              {/* Upload de Foto de Perfil - Disponível para todos os usuários */}
              <div className="mb-6">
                <ImageUpload
                  currentImageUrl={profilePhoto}
                  onImageUploaded={handlePhotoUploaded}
                  userId={currentUser.id}
                  folder="profiles"
                  label="Foto de Perfil"
                  aspectRatio="aspect-square"
                  maxSizeMB={3}
                  recommendedSize="400x400px"
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Sua foto será comprimida automaticamente para melhor desempenho
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo
                  </label>
                  <input
                    {...register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    {...register('phone')}
                    placeholder="(00) 00000-0000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              {/* Campos de Profissional - Apenas para owners */}
              {currentUser.role === 'owner' && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Profissionais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Especialidade
                      </label>
                      <input
                        {...register('specialty')}
                        placeholder="Ex: Cortes Clássicos, Barba, etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Sua especialidade será exibida nas barbearias onde você trabalha
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Experiência
                      </label>
                      <input
                        {...register('experience')}
                        placeholder="Ex: 5 anos, 10 meses, etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Tempo de experiência na profissão
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t border-gray-200">
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full sm:w-auto bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                >
                  {icons.trash({ className: 'h-4 w-4 inline mr-1' })}
                  Deletar Conta
                </Button>
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <Button type="button" variant="secondary" onClick={handleCancelEdit} className="w-full sm:w-auto">
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" className="w-full sm:w-auto">
                    Salvar Alterações
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Navegação em Abas */}
        <div className="border-b border-gray-300 mb-8">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-4 py-2 font-semibold rounded-t-lg transition-all ${
                activeTab === 'stats'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Estatísticas
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 font-semibold rounded-t-lg transition-all ${
                activeTab === 'history'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📋 Histórico ({userAppointments.length})
            </button>
            <button
              onClick={() => setActiveTab('waitlist')}
              className={`px-4 py-2 font-semibold rounded-t-lg transition-all ${
                activeTab === 'waitlist'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔔 Fila de Espera
            </button>
          </nav>
        </div>

        {/* Aba: Estatísticas */}
        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total de Agendamentos</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  {icons.calendar({ className: 'h-6 w-6 text-indigo-600' })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Concluídos</p>
                  <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  {icons.check({ className: 'h-6 w-6 text-green-600' })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Confirmados</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.confirmed}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  {icons.clock({ className: 'h-6 w-6 text-blue-600' })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pendentes</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  {icons.clock({ className: 'h-6 w-6 text-yellow-600' })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Cancelados</p>
                  <p className="text-3xl font-bold text-red-600">{stats.cancelled}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  {icons.x({ className: 'h-6 w-6 text-red-600' })}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Gasto</p>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.totalSpent.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-2xl">💰</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Gasto Médio</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats.avgSpent.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:col-span-2 lg:col-span-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Serviço Mais Utilizado</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.mostUsedService || 'Nenhum serviço ainda'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <span className="text-2xl">✂️</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gráfico de Agendamentos Mensais */}
        {activeTab === 'stats' && stats.monthlyAppointments.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6">📈 Agendamentos dos Últimos 6 Meses</h3>
            <div className="space-y-4">
              {stats.monthlyAppointments.map(({ month, count, monthName }) => {
                const maxCount = Math.max(...stats.monthlyAppointments.map(m => m.count)) || 1;
                const percentage = (count / maxCount) * 100;
                
                return (
                  <div key={month} className="flex items-center space-x-4">
                    <div className="w-16 text-sm font-medium text-gray-700 text-right">
                      {monthName}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      >
                        {count > 0 && (
                          <span className="text-white text-sm font-semibold">{count}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Barbearia Favorita */}
        {activeTab === 'stats' && stats.mostVisited && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🏆 Barbearia Favorita</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img
                  src={stats.mostVisited.imageUrl}
                  alt={stats.mostVisited.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div>
                  <p className="font-semibold text-gray-900">{stats.mostVisited.name}</p>
                  <p className="text-sm text-gray-600">{stats.mostVisited.city}</p>
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={() => navigate(`/barbershop/${stats.mostVisited.id}`)}
              >
                Visitar
              </Button>
            </div>
          </div>
        )}

        {/* Aba: Histórico */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Histórico de Agendamentos</h3>
            
            {userAppointments.length > 0 ? (
              <div className="space-y-4">
                {userAppointments.map(app => {
                  const barbershop = barbershops.find(b => b.id === app.barbershopId);
                  
                  return (
                    <div
                      key={app.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{barbershop?.name || 'Barbearia'}</p>
                          <p className="text-sm text-gray-600">{app.service}</p>
                        </div>
                        {getStatusBadge(app.status)}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
                        <div className="flex items-center">
                          {icons.calendar({ className: 'h-4 w-4 mr-1' })}
                          {new Date(app.date + 'T12:00:00').toLocaleDateString('pt-BR')}
                        </div>
                        <div className="flex items-center">
                          {icons.clock({ className: 'h-4 w-4 mr-1' })}
                          {app.time}
                        </div>
                      </div>

                      {app.notes && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-sm text-gray-700">
                          <span className="font-semibold">Obs:</span> {app.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-5xl mb-4">📅</div>
                <p className="text-gray-500">Você ainda não tem agendamentos</p>
                <Button
                  variant="primary"
                  className="mt-4"
                  onClick={() => navigate('/')}
                >
                  Buscar Barbearias
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Aba: Fila de Espera */}
        {activeTab === 'waitlist' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🔔 Minhas Filas de Espera</h3>
            
            {waitlist && waitlist.length > 0 ? (
              <div className="space-y-4">
                {waitlist.map(item => {
                  const barbershop = barbershops.find(b => b.id === item.barbershopId);
                  
                  return (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {barbershop?.imageUrl && (
                              <img
                                src={barbershop.imageUrl}
                                alt={barbershop.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            )}
                            <div>
                              <h4 className="font-bold text-gray-900">{barbershop?.name || 'Barbearia'}</h4>
                              <p className="text-sm text-gray-600">{barbershop?.city}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center">
                              {icons.calendar({ className: 'h-4 w-4 mr-1 text-indigo-600' })}
                              <span>{new Date(item.date + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                            </div>
                            <div className="flex items-center">
                              {icons.clock({ className: 'h-4 w-4 mr-1 text-indigo-600' })}
                              <span>{item.preferredTimes.length} horário(s) preferido(s)</span>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {item.preferredTimes.map((time, idx) => (
                              <span
                                key={idx}
                                className="bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-1 rounded"
                              >
                                {time}
                              </span>
                            ))}
                          </div>

                          <p className="text-xs text-gray-500">
                            Solicitado em: {item.createdAt?.toLocaleDateString('pt-BR')} às {item.createdAt?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>

                        <Button
                          variant="secondary"
                          onClick={() => {
                            if (window.confirm('Deseja sair da fila de espera?')) {
                              removeFromWaitlist(item.id);
                            }
                          }}
                        >
                          {icons.x({ className: 'h-4 w-4' })}
                        </Button>
                      </div>

                      <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-xs text-blue-800">
                          💡 <strong>Status:</strong> Você será notificado quando um horário for liberado nesta data.
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔔</div>
                <p className="text-gray-500 mb-4">Você não está em nenhuma fila de espera</p>
                <p className="text-sm text-gray-400 mb-6">
                  Quando não houver horários disponíveis, você pode entrar na fila de espera<br />
                  e ser notificado quando um horário for liberado.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/')}
                >
                  Buscar Barbearias
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Modal de Confirmação de Deleção */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="⚠️ Deletar Conta Permanentemente"
        >
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {icons.alert({ className: 'h-6 w-6 text-red-500' })}
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-bold text-red-800 mb-2">
                    Esta ação é IRREVERSÍVEL!
                  </h3>
                  <p className="text-sm text-red-700">
                    Ao deletar sua conta, os seguintes dados serão permanentemente removidos:
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✖</span>
                  <span><strong>Histórico completo</strong> de agendamentos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✖</span>
                  <span><strong>Estatísticas</strong> e dados de uso</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✖</span>
                  <span><strong>Dados pessoais</strong> (nome, telefone, email)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✖</span>
                  <span><strong>Fila de espera</strong> em barbearias</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✖</span>
                  <span><strong>Notificações</strong> e preferências</span>
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Importante:</strong> Não será possível recuperar nenhum dado após a confirmação.
                Se você tiver agendamentos futuros, eles serão cancelados automaticamente.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Deletando...
                  </>
                ) : (
                  <>
                    {icons.trash({ className: 'h-4 w-4 inline mr-1' })}
                    Sim, Deletar Minha Conta
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ProfilePage;
